import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import defaultLogo from "../../ASSETS/LOGO/defaultLogo.svg";
import secondLogo from "../../ASSETS/LOGO/secondLogo.svg";
import styles from "./NavBar.module.css";
import { megaMenus } from "../../CONTENT/navigation";
import { EASE } from "../../UTILS/ANIMATION/motionVariants";

const megaPanel = {
  hidden: { opacity: 0, y: -12, clipPath: "inset(0% 0% 100% 0%)" },
  shown: {
    opacity: 1,
    y: 0,
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.45, ease: EASE, staggerChildren: 0.04, delayChildren: 0.08 },
  },
  exit: { opacity: 0, y: -8, clipPath: "inset(0% 0% 100% 0%)", transition: { duration: 0.22, ease: EASE } },
};
const megaItem = {
  hidden: { opacity: 0, y: 10 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
};
const sheet = {
  hidden: { opacity: 0, clipPath: "circle(0% at calc(100% - 40px) 40px)" },
  shown: {
    opacity: 1,
    clipPath: "circle(150% at calc(100% - 40px) 40px)",
    transition: { duration: 0.6, ease: EASE, staggerChildren: 0.05, delayChildren: 0.15 },
  },
  exit: { opacity: 0, clipPath: "circle(0% at calc(100% - 40px) 40px)", transition: { duration: 0.35, ease: EASE } },
};
const sheetItem = {
  hidden: { opacity: 0, y: 18 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};

const NavBar = () => {
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/home";
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const closeTimer = useRef(null);
  const triggerRefs = useRef({});
  const closeBtn = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (!isHome) {
      setScrolled(false);
      return undefined;
    }
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    if (mobileOpen) closeBtn.current?.focus();
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (openMenu) triggerRefs.current[openMenu]?.focus();
      setOpenMenu(null);
      setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openMenu]);

  const hoverOpen = (id) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(id);
  };
  const hoverClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 140);
  };
  const onBlurHeader = useCallback((e) => {
    if (!headerRef.current?.contains(e.relatedTarget)) setOpenMenu(null);
  }, []);

  const transparent = isHome && !scrolled && !openMenu;
  const bigLogo = isHome && !scrolled && !openMenu;

  return (
    <header
      ref={headerRef}
      className={`${styles.header} ${transparent ? styles.transparent : ""} ${mobileOpen ? styles.menuOpen : ""}`}
      onMouseLeave={hoverClose}
      onMouseEnter={() => clearTimeout(closeTimer.current)}
      onBlur={onBlurHeader}
    >
      <div className={styles.bar}>
        <Link to="/" className={styles.brand} aria-label="KUR Research home">
          <img
            src={bigLogo ? secondLogo : defaultLogo}
            alt=""
            className={`${styles.logo} ${bigLogo ? styles.logoLarge : ""}`}
          />
          <span
            className={`${styles.tagline} ${bigLogo ? "" : styles.taglineVisible}`}
          >
            Making <br /> Progress <br /> Possible
          </span>
        </Link>

        <nav className={styles.desktopNav} aria-label="Primary">
          <ul className={styles.list}>
            <li>
              <Link to="/" className={styles.link}>
                Home
              </Link>
            </li>
            {megaMenus.map((m) => (
              <li
                key={m.id}
                onMouseEnter={() => hoverOpen(m.id)}
              >
                <button
                  type="button"
                  ref={(el) => (triggerRefs.current[m.id] = el)}
                  className={`${styles.link} ${styles.trigger}`}
                  aria-expanded={openMenu === m.id}
                  aria-controls={`mega-${m.id}`}
                  onClick={() => setOpenMenu(openMenu === m.id ? null : m.id)}
                >
                  {m.label}
                  <span className={styles.chevron} aria-hidden="true" />
                </button>
                <AnimatePresence>
                  {openMenu === m.id && (
                    <motion.div
                      key="mega"
                      id={`mega-${m.id}`}
                      className={styles.mega}
                      variants={megaPanel}
                      initial="hidden"
                      animate="shown"
                      exit="exit"
                    >
                      <div className={styles.megaInner}>
                        <motion.div className={styles.megaIntro} variants={megaItem}>
                          <p className={styles.megaHeading}>{m.label}</p>
                          <p className={styles.megaDesc}>{m.description}</p>
                        </motion.div>
                        <ul className={styles.megaLinks}>
                          {m.links.map((l) => (
                            <motion.li key={l.href} variants={megaItem}>
                              <Link to={l.href} className={styles.megaLink}>
                                <span className={styles.megaLinkLabel}>{l.label}</span>
                                <span className={styles.megaLinkText}>{l.text}</span>
                              </Link>
                            </motion.li>
                          ))}
                        </ul>
                        <motion.div variants={megaItem} className={styles.featureWrap}>
                        <Link to={m.feature.href} className={styles.feature}>
                          <span className={styles.featureLabel}>{m.feature.label}</span>
                          <span className={styles.featureText}>{m.feature.text}</span>
                          <span aria-hidden="true" className={styles.featureArrow}>
                            →
                          </span>
                        </Link>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            ))}
            <li>
              <Link to="/contact" className={styles.link}>
                Contact
              </Link>
            </li>
          </ul>
        </nav>

        <button
          type="button"
          className={styles.burger}
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          onClick={() => setMobileOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>


      <AnimatePresence>
      {mobileOpen && (
        <motion.div
          key="mobile-menu"
          variants={sheet}
          initial="hidden"
          animate="shown"
          exit="exit"
          id="mobile-menu"
          className={styles.mobile}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <button
            type="button"
            ref={closeBtn}
            className={styles.close}
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          >
            ×
          </button>
          <ul className={styles.mobileList}>
            <motion.li variants={sheetItem}>
              <Link to="/" className={styles.mobileTop}>
                Home
              </Link>
            </motion.li>
            {megaMenus.map((m) => (
              <motion.li key={m.id} variants={sheetItem}>
                <button
                  type="button"
                  className={styles.mobileTop}
                  aria-expanded={mobileSection === m.id}
                  onClick={() =>
                    setMobileSection(mobileSection === m.id ? null : m.id)
                  }
                >
                  {m.label}
                  <span className={styles.chevron} aria-hidden="true" />
                </button>
                {mobileSection === m.id && (
                  <ul className={styles.mobileSub}>
                    {m.links.map((l) => (
                      <li key={l.href}>
                        <Link to={l.href}>{l.label}</Link>
                      </li>
                    ))}
                    <li>
                      <Link to={m.feature.href} className={styles.mobileFeature}>
                        {m.feature.label} →
                      </Link>
                    </li>
                  </ul>
                )}
              </motion.li>
            ))}
            <motion.li variants={sheetItem}>
              <Link to="/contact" className={styles.mobileTop}>
                Contact
              </Link>
            </motion.li>
          </ul>
        </motion.div>
      )}
      </AnimatePresence>
    </header>
  );
};

export default NavBar;
